import { apiClient } from '../client';
import type {
  CreateResaleItemPayload,
  CreateResaleItemResponse,
  ResaleItemsListParams,
  ResaleItemsListResponse,
  UpdateResaleItemPayload,
  UpdateResaleItemResponse,
} from './types';

const MULTIPART_CONFIG = { headers: { 'Content-Type': 'multipart/form-data' } };

type ResaleItemPayload = CreateResaleItemPayload | UpdateResaleItemPayload;

function hasFiles(payload: ResaleItemPayload): boolean {
  return Array.isArray(payload.images) && payload.images.length > 0;
}

function hasImageChanges(payload: UpdateResaleItemPayload): boolean {
  return hasFiles(payload) || (payload.remove_image_ids?.length ?? 0) > 0;
}

function buildFormData(payload: ResaleItemPayload, prefix = 'resale_item'): FormData {
  const formData = new FormData();

  for (const [key, value] of Object.entries(payload)) {
    if (key === 'images') {
      (value as File[]).forEach((file) => {
        formData.append(`${prefix}[images][]`, file);
      });
    } else if (key === 'remove_image_ids') {
      (value as string[]).forEach((id) => {
        formData.append(`${prefix}[remove_image_ids][]`, id);
      });
    } else if (value === null) {
      // Explicit null = "clear this field". FormData can only carry strings,
      // so we send '' and let Rails interpret it as a cleared value (matches
      // the JSON path's behaviour, where axios serialises null literally).
      formData.append(`${prefix}[${key}]`, '');
    } else if (value !== undefined) {
      formData.append(`${prefix}[${key}]`, String(value));
    }
  }

  return formData;
}

export const resaleItems = {
  list: (params?: ResaleItemsListParams) =>
    apiClient.get<ResaleItemsListResponse>('/api/resale_items', { params }),

  create: (data: CreateResaleItemPayload) => {
    if (hasFiles(data)) {
      return apiClient.post<CreateResaleItemResponse>(
        '/api/resale_items',
        buildFormData(data),
        MULTIPART_CONFIG,
      );
    }
    return apiClient.post<CreateResaleItemResponse>('/api/resale_items', { resale_item: data });
  },

  update: (id: number, data: UpdateResaleItemPayload) => {
    if (hasImageChanges(data)) {
      return apiClient.patch<UpdateResaleItemResponse>(
        `/api/resale_items/${id}`,
        buildFormData(data),
        MULTIPART_CONFIG,
      );
    }
    return apiClient.patch<UpdateResaleItemResponse>(`/api/resale_items/${id}`, { resale_item: data });
  },

  activate: (id: number) =>
    apiClient.post<UpdateResaleItemResponse>(`/api/resale_items/${id}/activate`, {}),

  deactivate: (id: number) =>
    apiClient.post<UpdateResaleItemResponse>(`/api/resale_items/${id}/deactivate`, {}),
};
